import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
    FaTimes,
} from 'react-icons/fa';
import { useI18n } from '../../i18n';
import { useFileUpload } from '../../hooks/useFileUpload';
import MeetingMembersSelect from './MeetingMembersSelect';
import ExternalGuestsInput from './ExternalGuestsInput';
import DatePickerInput from '../Common/DatePickerInput';
import TimePickerInput from '../Common/TimePickerInput';
import RecurrenceModal from './RecurrenceModal';
import ScheduleAvailabilityCalendar from './ScheduleAvailabilityCalendar';
import meetingService from '../../services/meetingService';
import { notify } from '../CustomToast';
import occurrenceDropdownIcon from '../../assets/dropdown-chevron.svg';
import attachmentsIcon from '../../assets/upload-simple.svg';
import descriptionIcon from '../../assets/list-plus.svg';
import '../Projects/ProjectComposer.css';
import './ScheduleMeetingLandingModal.css';

const DEFAULT_RECURRENCE = {
    frequency: 'once',
    interval: 1,
    days_of_week: [],
    recurrence_end_date: null,
    occurrences: null,
};

const getOccurrenceSelection = (recurrence) => (
    recurrence?.frequency && recurrence.frequency !== 'once'
        ? 'set_reoccurrence'
        : 'does_not_repeat'
);

const getRecurrenceLabel = (recurrence) => {
    if (!recurrence || recurrence.frequency === 'once') return null;
    const interval = recurrence.interval || 1;
    if (recurrence.frequency === 'daily') {
        return interval === 1 ? 'Daily' : `Every ${interval} Days`;
    }
    if (recurrence.frequency === 'weekly') {
        const days = recurrence.days_of_week || [];
        const suffix = interval > 1 ? ` (every ${interval} weeks)` : '';
        if (days.length === 0) return `Weekly${suffix}`;
        if (days.length === 7) return `Daily${suffix}`;
        if (days.length === 5 && !days.includes('Sat') && !days.includes('Sun'))
            return `Weekdays${suffix}`;
        if (days.length === 2 && days.includes('Sat') && days.includes('Sun'))
            return `Weekends${suffix}`;
        return `Weekly on ${days.join(', ')}${suffix}`;
    }
    if (recurrence.frequency === 'monthly') {
        if (interval % 12 === 0) {
            const years = interval / 12;
            return years === 1 ? 'Yearly' : `Every ${years} Years`;
        }
        return interval === 1 ? 'Monthly' : `Every ${interval} Months`;
    }
    return 'Custom';
};

// Helper: Get today's date at midnight
const getTodayAtMidnight = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
};

// Helper: Get current time (hours and minutes only)
const getCurrentTime = () => {
    const now = new Date();
    const time = new Date();
    time.setHours(now.getHours(), now.getMinutes(), 0, 0);
    return time;
};

// Helper: Check if time is in the past for today
const isTimeInPast = (selectedDate, selectedTime) => {
    if (!selectedDate || !selectedTime) return false;
    const today = getTodayAtMidnight();
    const selectedDateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());

    // If selected date is today, check if time is in past
    if (selectedDateOnly.getTime() === today.getTime()) {
        const now = new Date();
        const selectedTimeObj = new Date(now.getFullYear(), now.getMonth(), now.getDate(), selectedTime.getHours(), selectedTime.getMinutes());
        return selectedTimeObj < now;
    }
    return false;
};

const ScheduleMeetingLandingModal = ({ isOpen, onClose, onSuccess }) => {
    const { t } = useI18n();
    const { uploadedFiles, uploadFiles, removeFile, clearAll } = useFileUpload();

    const [activeTab, setActiveTab] = useState('details');
    const [isRecurrenceModalOpen, setIsRecurrenceModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [errors, setErrors] = useState({});
    const [occurrenceSelection, setOccurrenceSelection] = useState('does_not_repeat');
    const latestRecurrenceRef = useRef({ ...DEFAULT_RECURRENCE });

    const [formData, setFormData] = useState({
        title: '',
        start_at: null,
        start_time: null,
        end_at: null,
        end_time: null,
        location: '',
        is_online: false,
        member_ids: [],
        guest_emails: [],
        description: '',
        recurrence: { ...DEFAULT_RECURRENCE }
    });

    // Reset form and fetch available users when modal opens
    useEffect(() => {
        if (isOpen) {
            resetForm();
            fetchAvailableUsers();
        }
    }, [isOpen]);

    const fetchAvailableUsers = async () => {
        setLoadingUsers(true);
        try {
            const response = await meetingService.getAvailableUsers();
            // API response structure: { code, status, message, data: { users: [...] } }
            const users = response?.data?.users || [];
            setAvailableUsers(users);
        } catch (err) {
            console.error('Error fetching users:', err);
            notify('error', t('common.messages.error_loading_users') || 'Error loading users');
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    const handleTitleChange = (e) => {
        setFormData(prev => ({ ...prev, title: e.target.value }));
        if (errors.title) setErrors(prev => ({ ...prev, title: null }));
    };

    const handleStartDateChange = (date) => {
        setFormData(prev => ({
            ...prev,
            start_at: date,
            end_at: prev.end_at || date
        }));
        if (errors.start_at) setErrors(prev => ({ ...prev, start_at: null }));
    };

    const handleEndDateChange = (date) => {
        setFormData(prev => ({ ...prev, end_at: date }));
        if (errors.end_at) setErrors(prev => ({ ...prev, end_at: null }));
    };

    const handleStartTimeChange = (time) => {
        setFormData(prev => ({ ...prev, start_time: time }));
        if (errors.start_time) setErrors(prev => ({ ...prev, start_time: null }));
    };

    const handleEndTimeChange = (time) => {
        setFormData(prev => ({ ...prev, end_time: time }));
        if (errors.end_time) setErrors(prev => ({ ...prev, end_time: null }));
    };

    const handleLocationChange = (e) => {
        setFormData(prev => ({ ...prev, location: e.target.value }));
    };

    const handleOnlineMeetingToggle = () => {
        setFormData(prev => ({
            ...prev,
            is_online: !prev.is_online,
        }));
    };

    // const handleMemberToggle = (userId) => {
    //     setFormData(prev => {
    //         const isSelected = prev.member_ids.some(id => String(id) === userId);
    //         const newMemberIds = isSelected
    //             ? prev.member_ids.filter(id => String(id) !== userId)
    //             : [...prev.member_ids, userId];
    //         return { ...prev, member_ids: newMemberIds };
    //     });
    //     if (errors.member_ids) setErrors(prev => ({ ...prev, member_ids: null }));
    // };

    const handleMemberToggle = (userId) => {
        setFormData(prev => {
            const isSelected = prev.member_ids.some(id => String(id) === userId);
            const newMemberIds = isSelected
                ? prev.member_ids.filter(id => String(id) !== userId)
                : [...prev.member_ids, userId];
            // console.log('prev:', prev.member_ids, '→ next:', newMemberIds); // ADD THIS
            return { ...prev, member_ids: newMemberIds };
        });
        if (errors.member_ids) setErrors(prev => ({ ...prev, member_ids: null }));
    };

    const handleDescriptionChange = (e) => {
        setFormData(prev => ({ ...prev, description: e.target.value }));
    };

    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files);
        await uploadFiles(files);
    };

    const handleRecurrenceSave = (recurrenceData) => {
        const normalizedRecurrence = {
            ...DEFAULT_RECURRENCE,
            ...recurrenceData,
            recurrence_end_date: recurrenceData?.recurrence_end_date ?? recurrenceData?.end_date ?? null,
        };
        latestRecurrenceRef.current = normalizedRecurrence;

        delete normalizedRecurrence.end_date;

        setFormData(prev => ({
            ...prev,
            recurrence: normalizedRecurrence
        }));
        setOccurrenceSelection(getOccurrenceSelection(normalizedRecurrence));
    };

    const handleOccurrenceChange = (e) => {
        const selectedValue = e.target.value;
        setOccurrenceSelection(selectedValue);

        if (selectedValue === 'does_not_repeat') {
            setFormData(prev => ({
                ...prev,
                recurrence: { ...DEFAULT_RECURRENCE }
            }));
            return;
        }

        setIsRecurrenceModalOpen(true);
    };

    const handleRecurrenceModalClose = () => {
        setIsRecurrenceModalOpen(false);
        setOccurrenceSelection(getOccurrenceSelection(latestRecurrenceRef.current));
    };

    const hasFilesUploading = () => {
        return uploadedFiles.some(f => f.status === 'uploading');
    };

    // Reset form to initial state
    const resetForm = () => {
        setFormData({
            title: '',
            start_at: null,
            start_time: null,
            end_at: null,
            end_time: null,
            location: '',
            is_online: false,
            member_ids: [],
            guest_emails: [],
            description: '',
            recurrence: { ...DEFAULT_RECURRENCE }
        });
        latestRecurrenceRef.current = { ...DEFAULT_RECURRENCE };
        setErrors({});
        setActiveTab('details');
        setOccurrenceSelection('does_not_repeat');
        clearAll();
    };

    const handleSubmit = async () => {
        setErrors({});

        // Client-side validation
        const newErrors = {};
        if (!formData.title.trim()) newErrors.title = [t('validation.required') || 'This field is required'];
        if (!formData.start_at) newErrors.start_at = [t('validation.required') || 'This field is required'];
        if (!formData.start_time) newErrors.start_time = [t('validation.required') || 'This field is required'];
        if (!formData.end_at) newErrors.end_at = [t('validation.required') || 'This field is required'];
        if (!formData.end_time) newErrors.end_time = [t('validation.required') || 'This field is required'];
        if (formData.member_ids.length === 0 && formData.guest_emails.length === 0) {
            newErrors.member_ids = [t('validation.required') || 'Please invite at least one member or external guest'];
        }
        if ((formData.member_ids.length + formData.guest_emails.length) > 10) newErrors.guest_emails = [t('validation.max_members') || 'Maximum 10 invitees allowed'];

        // Validate start date is not in past
        if (formData.start_at) {
            const today = getTodayAtMidnight();
            if (new Date(formData.start_at) < today) {
                newErrors.start_at = [t('pages.calendar.schedule.validation.start_date_past') || 'Start date cannot be in the past'];
            }
        }

        // Validate start time is not in past (if today is selected)
        if (formData.start_at && formData.start_time && isTimeInPast(formData.start_at, formData.start_time)) {
            newErrors.start_time = [t('pages.calendar.schedule.validation.start_time_past') || 'Start time cannot be in the past'];
        }

        // Validate end date is not before start date
        if (formData.start_at && formData.end_at) {
            if (new Date(formData.end_at) < new Date(formData.start_at)) {
                newErrors.end_at = [t('pages.calendar.schedule.validation.end_date_before_start') || 'End date cannot be before start date'];
            }
        }

        // Validate end time is after start time (if same day)
        if (formData.start_at && formData.end_at && formData.start_time && formData.end_time) {
            const startDateOnly = new Date(formData.start_at.getFullYear(), formData.start_at.getMonth(), formData.start_at.getDate());
            const endDateOnly = new Date(formData.end_at.getFullYear(), formData.end_at.getMonth(), formData.end_at.getDate());

            if (startDateOnly.getTime() === endDateOnly.getTime()) {
                // Same day: end time must be after start time
                if (formData.end_time <= formData.start_time) {
                    newErrors.end_time = [t('pages.calendar.schedule.validation.end_time_before_start') || 'End time must be after start time'];
                }
            }
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsSubmitting(true);

        try {
            // Get uploaded file IDs
            const attachmentIds = uploadedFiles
                .filter(f => f.status === 'uploaded')
                .map(f => f.uploadId);

            // Combine date and time into ISO strings
            const combineDateTime = (date, time) => {
                if (!date || !time) return null;
                const combined = new Date(date);
                combined.setHours(time.getHours(), time.getMinutes(), 0, 0);
                return combined.toISOString();
            };

            // Prepare payload
            const payload = {
                title: formData.title,
                start_at: combineDateTime(formData.start_at, formData.start_time),
                end_at: combineDateTime(formData.end_at, formData.end_time),
                location: formData.location || null,
                is_online: formData.is_online,
                description: formData.description || null,
                member_ids: formData.member_ids,
                guest_emails: formData.guest_emails,
                recurrence: formData.recurrence,
                attachment_ids: attachmentIds.length > 0 ? attachmentIds : null,
            };

            const response = await meetingService.createMeeting(payload);

            // All responses now use CustomerResponseTrait format: { code, status, message, data }
            if (response.status === true) {
                notify('success', response.message || t('pages.calendar.schedule.meeting_created') || 'Meeting created successfully');
                resetForm();
                if (onSuccess) onSuccess(response.data);
                onClose();
            } else {
                notify('error', response.message || t('common.messages.error') || 'An error occurred');
            }
        } catch (err) {
            if (err.status === 422 && err.errors) {
                setErrors(err.errors);
            } else {
                notify('error', err.message || t('common.messages.error') || 'An error occurred');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const renderTabs = () => (
        <div className="sml-tabs-header">
            <button
                className={`sml-tab-btn ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTab('details')}
            >
                {t('pages.calendar.details.meeting_details') || 'Meeting Details'}
            </button>
            <button
                className={`sml-tab-btn ${activeTab === 'attachments' ? 'active' : ''}`}
                onClick={() => setActiveTab('attachments')}
            >
                {t('pages.calendar.schedule.attachments') || 'Attachments'}
            </button>
        </div>
    );

    const renderDetailsTab = () => (
        <div className={`sml-tab-content ${activeTab === 'details' ? 'active' : ''}`}>
            <div className="sml-details-grid">
                {/* Meeting Title */}
                <div className="project-form-group full-width">
                    <label className="project-form-label">{t('pages.calendar.details.meeting_title') || 'Meeting Title'} <span className="required">*</span></label>
                    <input
                        type="text"
                        className={`project-form-input ${errors.title ? 'error' : ''}`}
                        placeholder={t('pages.calendar.schedule.title_placeholder') || 'e.g. Meeting with Dev Team'}
                        value={formData.title}
                        onChange={handleTitleChange}
                    />
                    {errors.title && <div className="project-form-error">{errors.title[0]}</div>}
                </div>

                {/* Description */}
                <div className="project-form-group full-width">
                    <label className="project-form-label">{t('pages.calendar.schedule.description') || 'Description'}</label>
                    <textarea
                        className="project-form-textarea"
                        placeholder={t('pages.calendar.schedule.description_placeholder') || 'Type details for this meeting...'}
                        value={formData.description}
                        onChange={handleDescriptionChange}
                    ></textarea>
                </div>

                {/* Date and Time Row */}
                <div className="sml-datetime-row">
                    <div className="project-form-group">
                        <label className="project-form-label">{t('pages.calendar.schedule.start_date') || 'Start Date'} <span className="required">*</span></label>
                        <DatePickerInput
                            selected={formData.start_at}
                            onChange={handleStartDateChange}
                            minDate={getTodayAtMidnight()}
                            isInvalid={!!errors.start_at}
                            placeholder={t('pages.calendar.schedule.start_date_placeholder') || 'Select date'}
                        />
                        {errors.start_at && <div className="project-form-error">{errors.start_at[0]}</div>}
                    </div>

                    <div className="project-form-group">
                        <label className="project-form-label">{t('pages.calendar.schedule.start_time') || 'Start Time'} <span className="required">*</span></label>
                        <TimePickerInput
                            selected={formData.start_time}
                            onChange={handleStartTimeChange}
                            isInvalid={!!errors.start_time}
                            placeholder={t('pages.calendar.schedule.start_time_placeholder') || 'Select time'}
                        />
                        {errors.start_time && <div className="project-form-error">{errors.start_time[0]}</div>}
                    </div>

                    <div className="project-form-group">
                        <label className="project-form-label">{t('pages.calendar.schedule.end_date') || 'End Date'} <span className="required">*</span></label>
                        <DatePickerInput
                            selected={formData.end_at}
                            onChange={handleEndDateChange}
                            minDate={formData.start_at || getTodayAtMidnight()}
                            isInvalid={!!errors.end_at}
                            placeholder={t('pages.calendar.schedule.end_date_placeholder') || 'Select date'}
                        />
                        {errors.end_at && <div className="project-form-error">{errors.end_at[0]}</div>}
                    </div>

                    <div className="project-form-group">
                        <label className="project-form-label">{t('pages.calendar.schedule.end_time') || 'End Time'} <span className="required">*</span></label>
                        <TimePickerInput
                            selected={formData.end_time}
                            onChange={handleEndTimeChange}
                            isInvalid={!!errors.end_time}
                            placeholder={t('pages.calendar.schedule.end_time_placeholder') || 'Select time'}
                        />
                        {errors.end_time && <div className="project-form-error">{errors.end_time[0]}</div>}
                    </div>

                    <div className="project-form-group">
                        <label className="project-form-label">{t('pages.calendar.schedule.occurrence') || 'Occurrence'} <span className="required">*</span></label>
                        <select
                            className="project-form-select sml-occurrence-select"
                            value={occurrenceSelection}
                            onChange={handleOccurrenceChange}
                            style={{ backgroundImage: `url("${occurrenceDropdownIcon}")` }}
                        >
                            <option value="does_not_repeat">
                                {t('pages.calendar.schedule.does_not_repeat') || 'Does not repeat'}
                            </option>
                            <option value="set_reoccurrence">
                                {t('pages.calendar.schedule.set_reoccurrence') || 'Set reoccurrence'}
                            </option>
                        </select>
                        {occurrenceSelection === 'set_reoccurrence' && getRecurrenceLabel(formData.recurrence) && (
                            <div className="sml-recurrence-summary" onClick={() => setIsRecurrenceModalOpen(true)}>
                                {getRecurrenceLabel(formData.recurrence)}
                            </div>
                        )}
                    </div>
                </div>

                {/* Location & Online Row */}
                <div className="sml-location-row">
                    <label className="project-form-label">{t('pages.calendar.schedule.location') || 'Add Location:'}</label>
                    <div className="sml-location-controls">
                        <div className="project-form-group location-group">
                            <input
                                type="text"
                                className="project-form-input"
                                placeholder={t('pages.calendar.schedule.location_placeholder') || 'Add location'}
                                value={formData.location}
                                onChange={handleLocationChange}
                                maxLength={150}
                            />
                        </div>
                        <div className="sml-toggle-group">
                            <div
                                className={`sml-toggle-switch ${formData.is_online ? 'active' : ''}`}
                                onClick={handleOnlineMeetingToggle}
                                role="switch"
                                aria-checked={formData.is_online}
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleOnlineMeetingToggle();
                                    }
                                }}
                            >
                                <div className="sml-toggle-handle"></div>
                            </div>
                            <span className="sml-toggle-label">{t('pages.calendar.schedule.online_meeting') || 'Online Meeting'}</span>
                        </div>
                    </div>
                </div>

                {/* Invite Members */}
                <div className="sml-input-group full-width">
                    <label className="project-form-label">{t('pages.calendar.schedule.invite_members') || 'Invite Members'} <span className="required">*</span></label>
                    <MeetingMembersSelect
                        users={availableUsers}
                        selectedUserIds={formData.member_ids}
                        onToggle={handleMemberToggle}
                        error={errors.member_ids ? errors.member_ids[0] : null}
                        loading={loadingUsers}
                        placeholder={t('pages.calendar.schedule.invite_members_placeholder') || 'Search by email'}
                    />
                </div>

                <ExternalGuestsInput
                    guestEmails={formData.guest_emails}
                    onChange={(guestEmails) => {
                        setFormData(prev => ({ ...prev, guest_emails: guestEmails }));
                        if (errors.guest_emails) setErrors(prev => ({ ...prev, guest_emails: null }));
                    }}
                    registeredUsers={availableUsers}
                    error={errors.guest_emails ? errors.guest_emails[0] : null}
                    label={t('pages.calendar.schedule.external_guests') || 'Invite Members (Outside Organization)'}
                    placeholder={t('pages.calendar.schedule.external_guests_placeholder') || 'Search by email'}
                />
            </div>
        </div>
    );

    const renderAttachmentsTab = () => (
        <div className={`sml-tab-content ${activeTab === 'attachments' ? 'active' : ''}`}>
            <div className="sml-attachments-grid">
                {/* Attachment Upload */}
                <div className="sml-input-group sml-leading-icon-group">
                    <label className="project-form-label">{t('pages.calendar.schedule.attachments') || 'Attachments'}</label>
                    <div className="sml-leading-icon-field">
                        <img src={attachmentsIcon} alt="" className="sml-leading-icon" aria-hidden="true" />
                        <div className="sml-upload-section">
                            <label className="sml-upload-input-label">
                                <span>{t('pages.calendar.schedule.upload_document') || 'Upload Document'}</span>
                                <input
                                    type="file"
                                    multiple
                                    onChange={handleFileSelect}
                                    className="sml-upload-input-hidden"
                                />
                            </label>
                            {uploadedFiles.length > 0 && (
                                <div className="sml-uploaded-files">
                                    {uploadedFiles.map(file => (
                                        <div key={file.id} className="sml-file-item">
                                            <div className="sml-file-info">
                                                <span className="sml-file-name">{file.name}</span>
                                                <span className="sml-file-status">
                                                    {file.status === 'uploading' && `${file.progress}%`}
                                                    {file.status === 'uploaded' && '✓'}
                                                    {file.status === 'error' && '✗'}
                                                </span>
                                            </div>
                                            <button
                                                className="sml-file-remove-btn"
                                                onClick={() => removeFile(file.id, file.uploadId)}
                                                disabled={file.status === 'uploading'}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(
        <div className="project-composer-overlay sml-overlay" onClick={handleBackdropClick}>
            <div className="project-composer-modal sml-modal-container">
                {/* Header */}
                <div className="project-composer-header">
                    <h2 className="project-composer-title sml-title">{t('pages.calendar.schedule.title') || 'Schedule Meeting'}</h2>
                    <button className="project-composer-close" onClick={onClose}>
                        <FaTimes size={16} />
                    </button>
                </div>

                <div className="project-composer-body sml-body">
                    {/* Tabs Section - Left */}
                    <div className="sml-tabs-section">
                        {renderTabs()}
                        {renderDetailsTab()}
                        {renderAttachmentsTab()}
                    </div>

                    {/* Find a Time Section - Right */}
                    <div className="sml-find-time-section">
                        <h3 className="sml-find-time-heading">{t('pages.calendar.schedule.find_a_time') || 'Find a Time'}</h3>
                        <ScheduleAvailabilityCalendar
                            memberIds={formData.member_ids}
                            availableUsers={availableUsers}
                            startDate={formData.start_at}
                            startTime={formData.start_time}
                            endDate={formData.end_at}
                            endTime={formData.end_time}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="project-composer-footer">
                    <button className="project-composer-btn project-composer-btn-secondary" onClick={onClose}>
                        {t('common.actions.cancel') || 'Cancel'}
                    </button>
                    <button
                        className="project-composer-btn project-composer-btn-primary"
                        onClick={handleSubmit}
                        disabled={isSubmitting || hasFilesUploading()}
                    >
                        {isSubmitting ? t('common.actions.saving') || 'Saving...' : t('common.actions.save') || 'Save'}
                    </button>
                </div>

                <RecurrenceModal
                    isOpen={isRecurrenceModalOpen}
                    onClose={handleRecurrenceModalClose}
                    onSave={handleRecurrenceSave}
                    initialData={formData.recurrence}
                />
            </div>
        </div>,
        document.body
    );
};

export default ScheduleMeetingLandingModal;
